import { createSlice } from '@reduxjs/toolkit';
import { mergeReduxStateData } from 'library/helpers/reduxHelpers';

const initialState = {
	data: {},
	loading: false,
	error: null,
	timeStamp: false,
	searchable: true,

	//✅ Complete States
	peopleLoadingComplete: false,
	disablePersonComplete: false,
	deletePersonComplete: false,
	enablePersonComplete: false,
	inviteAccessOnlyPeopleComplete: false,
	completeAddProfile: false,

	// ✅ Loading States
	loadingAddress: false,
	loadingContacts: false,
	loadingBookings: false,
	loadingTeams: false,
	loadingPermissions: false,
	loadingUpdateProfile: false,
	loadingPhotoProfile: false,
	loadingAddProfile: false,
	loadingEnableProfile: false,
	loadingDisableProfile: false,
	loadingDeleteProfile: false,
	loadingResetPassword: false,
	loadingInviteAccessOnlyPeople: false,
	loadingUpdateContact: false,

	// ✅ Selected Users for Invitation
	selectedUsers: []
};

const peopleSlice = createSlice({
	name: 'people',
	initialState,
	reducers: {
		// Action for initial load
		loadPerson(state, action) {
			state.loading = true;
			state.error = null;
		},
		loadPersonSuccess(state, action) {
			state.timeStamp = Date.now();
			state.loading = false;
			const orgID = action.payload.orgID;
			const oldData = state.data[orgID] || [];

			// Merge sites data using 'personID' as the unique identifier
			state.data[orgID] = mergeReduxStateData(oldData, action.payload.data, 'personID');

			state.peopleLoadingComplete = true;
		},
		loadPersonError(state, action) {
			state.loading = false;
			state.error = action.payload;
		},
		loadPersonContact(state) {
			state.error = null;
			state.loadingContacts = true;
		},
		loadPersonContactSuccess(state, action) {
			const orgID = action.payload.orgID;
			state.data[orgID] = state.data[orgID].map((person) =>
				person.personID === action.payload.personID
					? { ...person, contacts: { data: action.payload.data, timeStamp: Date.now() } }
					: person
			);
			state.loadingContacts = false;
		},
		loadPersonContactError(state, action) {
			state.error = action.payload;
			state.loadingContacts = false;
		},
		// add person contact
		addPersonContact(state) {
			state.loadingContacts = true;
			state.error = null;
		},
		addPersonContactSuccess(state, action) {
			state.loadingContacts = false;
			state.error = null;
		},
		addPersonContactError(state, action) {
			state.loadingContacts = false;
			state.error = action.payload;
		},
		// update person contact
		updatePersonContact(state) {
			state.loadingUpdateContact = true;
			state.error = null;
		},
		updatePersonContactSuccess(state) {
			state.loadingUpdateContact = false;
			state.error = null;
		},
		updatePersonContactError(state, action) {
			state.loadingUpdateContact = false;
			state.error = action.payload;
		},
		loadPersonAddress(state) {
			state.loadingAddress = true;
			state.error = null;
		},
		loadPersonAddressSuccess(state, action) {
			const orgID = action.payload.orgID;
			state.data[orgID] = state.data[orgID].map((person) =>
				person.personID === action.payload.personID
					? { ...person, address: { data: action.payload.data, timeStamp: Date.now() } }
					: person
			);
			state.loadingAddress = false;
		},
		loadPersonAddressError(state, action) {
			state.error = action.payload;
			state.loadingAddress = false;
		},
		loadPersonBooking(state) {
			state.loadingBookings = true;
			state.error = null;
		},
		loadPersonBookingSuccess(state, action) {
			const orgID = action.payload.orgID;
			state.data[orgID] = state.data[orgID].map((person) =>
				person.personID === action.payload.personID
					? { ...person, bookings: { data: action.payload.data, timeStamp: Date.now() } }
					: person
			);
			state.loadingBookings = false;
		},
		loadPersonBookingError(state, action) {
			state.error = action.payload;
			state.loadingBookings = false;
		},
		loadPersonTeams(state) {
			state.loadingTeams = true;
			state.error = null;
		},
		loadPersonTeamsSuccess(state, action) {
			const orgID = action.payload.orgID;
			state.data[orgID] = state.data[orgID].map((person) =>
				person.personID === action.payload.personID
					? { ...person, teams: { data: action.payload.data, timeStamp: Date.now() } }
					: person
			);
			state.loadingTeams = false;
		},
		loadPersonTeamsError(state, action) {
			state.error = action.payload;
			state.loadingTeams = false;
		},
		loadPersonPermissions(state) {
			state.loadingPermissions = true;
			state.error = null;
		},
		loadPersonPermissionsSuccess(state, action) {
			const orgID = action.payload.orgID;
			state.data[orgID] = state.data[orgID].map((person) =>
				person.personID === action.payload.personID
					? { ...person, permissions: { data: action.payload.data, timeStamp: Date.now() } }
					: person
			);
			state.loadingPermissions = false;
		},
		loadPersonPermissionsError(state, action) {
			state.error = action.payload;
			state.loadingPermissions = false;
		},
		// Invite a multiple people
		invitePeople(state) {
			state.loadingAddProfile = true;
			state.completeAddProfile = false;
			state.error = null;
		},
		invitePeopleSuccess(state) {
			state.loadingAddProfile = false;
			state.completeAddProfile = true;
			state.error = null;
		},
		invitePeopleError(state, action) {
			state.loadingAddProfile = false;
			state.error = action.payload;
		},
		// Invite temp user then assign teams
		inviteAccessOnlyPeople(state) {
			state.loadingAddProfile = true;
			state.completeAddProfile = false;
			state.error = null;
		},
		inviteAccessOnlyPeopleSuccess(state) {
			state.loadingAddProfile = false;
			state.completeAddProfile = true;
			state.error = null;
		},
		inviteAccessOnlyPeopleError(state, action) {
			state.loadingAddProfile = false;
			state.completeAddProfile = false;
			state.error = action.payload;
		},
		// Action to set access only people to zezamii user
		setAccessOnlyToZezamiiUser(state) {
			state.loadingAddProfile = true;
			state.completeAddProfile = false;
			state.error = null;
		},
		setAccessOnlyToZezamiiUserSuccess(state) {
			state.loadingAddProfile = false;
			state.completeAddProfile = true;
			state.error = null;
		},
		setAccessOnlyToZezamiiUserError(state, action) {
			state.loadingAddProfile = false;
			state.completeAddProfile = false;
			state.error = action.payload;
		},
		// Action to update people
		updatePerson(state) {
			state.loadingUpdateProfile = true;
			state.error = null;
		},
		updatePersonSuccess(state, action) {
			state.loadingUpdateProfile = false;
			state.error = null;
		},
		updatePersonError(state, action) {
			state.loadingUpdateProfile = false;
			state.error = action.payload;
		},
		// ✅ Person Sites Actions
		updatePersonSites: (state) => {
			state.error = null;
		},
		updatePersonSitesError: (state, action) => {
			state.error = action.payload;
		},

		// Actions for enable
		enablePerson(state) {
			state.loadingEnableProfile = true;
			state.enablePersonComplete = false;
			state.error = null;
		},
		enablePersonSuccess(state, action) {
			state.loadingEnableProfile = false;
			state.enablePersonComplete = true;
			state.error = null;
		},
		enablePersonError(state, action) {
			state.loadingEnableProfile = false;
			state.enablePersonComplete = false;
			state.error = action.payload;
		},
		// Actions for disable
		disablePerson(state) {
			state.loadingDisableProfile = true;
			state.disablePersonComplete = false;
			state.error = null;
		},
		disablePersonSuccess(state, action) {
			state.loadingDisableProfile = false;
			state.disablePersonComplete = true;
			state.error = null;
		},
		disablePersonError(state, action) {
			state.loadingDisableProfile = false;
			state.disablePersonComplete = false;
			state.error = action.payload;
		},
		// Actions for reset password
		resetPersonPassword(state) {
			state.loadingResetPassword = true;
			state.error = null;
		},
		resetPersonPasswordSuccess(state) {
			state.loadingResetPassword = false;
			state.error = null;
		},
		resetPersonPasswordError(state, action) {
			state.loadingResetPassword = false;
			state.error = action.payload;
		},
		// Actions for delete
		deletePerson(state) {
			state.loadingDeleteProfile = true;
			state.error = null;
			state.deletePersonComplete = false;
		},
		deletePersonSuccess(state) {
			state.loadingDeleteProfile = false;
			state.error = null;
			state.deletePersonComplete = true;
		},
		deletePersonError(state, action) {
			state.loadingDeleteProfile = false;
			state.error = action.payload;
			state.deletePersonComplete = false;
		},
		// Actions for managing selected users for invitation
		addSelectedUser(state, action) {
			const newUser = action.payload;
			state.selectedUsers.push(newUser);
		},
		removeSelectedUser(state, action) {
			const { personID } = action.payload;
			state.selectedUsers = state.selectedUsers.filter((user) => user.personID !== personID);
		},
		updateSelectedUserStatus(state, action) {
			const { status, personID, newPersonID } = action.payload;

			state.selectedUsers = state.selectedUsers.map((user) =>
				user.personID === personID
					? {
							...user,
							status,
							personID: newPersonID || user.personID // Update with real personID if provided
					  }
					: user
			);
		},
		clearSelectedUsers(state) {
			state.selectedUsers = [];
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => {
			return initialState; // Reset state on logout
		});
	}
});

export const peopleActions = peopleSlice.actions;

export default peopleSlice.reducer;
